/**
 * The Findings Walkthrough's Next steps block: defaults and width maths.
 *
 * Deliberately a mirror of NEXT_STEPS_DEFAULTS / nextStepsWidths in
 * backend/utils/report-helpers.js. The settings panel shows these literals as
 * the value of every control, so if one side changes and the other does not,
 * the editor starts lying about what the report will look like -- the same
 * trade-off the rest of this widget's settings already carry.
 *
 * ABSENCE MEANS OFF in the renderers: a walkthrough with no `nextSteps` key
 * renders no block. That is what keeps a template saved before this existed --
 * which already carries the old caption and custom-table bands -- from
 * printing Next steps twice. New inserts write the key explicitly.
 */
export const NEXT_STEPS_PANELS = [
  { title: 'Prioritise',
    body: 'Remediate in severity order — Critical → High → Medium → Low — against the organisation’s internal SLA.' },
  { title: 'Retest',
    body: 'Request remediation verification once fixes are deployed; findings move to Verified Closed and the report is reissued.' },
  { title: 'Clean up',
    body: 'Disable test accounts and remove test records and uploaded files created during the engagement.' },
];

export const NEXT_STEPS_DEFAULTS = {
  enabled: true,
  caption: 'Next steps',
  captionColor: '#2e86c1',
  captionSize: 11,
  captionBold: true,
  captionCaps: false,
  panelBg: '#f2f5f9',
  titleColor: '#1f3864',
  titleSize: 9,
  titleBold: true,
  bodyColor: '#5f6b7a',
  bodySize: 8,
  borderColor: '#d8dfe8',
  showBorder: true,
  // Percent of the row's width between one panel and the next. Without it
  // the three fills touch and read as a single banded strip.
  panelGap: 2,
};

/** A fresh block, for the palette default and the house preset. */
export function nextStepsDefaultConfig() {
  return { ...NEXT_STEPS_DEFAULTS, panels: NEXT_STEPS_PANELS.map(p => ({ ...p })) };
}

/**
 * Panel widths as whole percentages that always total exactly 100 -- three
 * panels are 34/33/33, not 33/33/33, so the row has no hairline of unused
 * width down its right edge.
 */
export function nextStepsWidths(count, total) {
  if (count <= 0) return [];
  const span = Number.isFinite(Number(total)) ? Number(total) : 100;
  const base = Math.floor(span / count);
  const widths = new Array(count).fill(base);
  let remainder = span - base * count;
  for (let i = 0; remainder > 0; i = (i + 1) % count, remainder--) widths[i] += 1;
  return widths;
}

/**
 * The row as panels plus the gap columns between them. A gap is a real
 * column, not padding: neither a Word table nor the PDF table util can put
 * space BETWEEN two filled cells, and padding only moves the text while the
 * fills stay touching. Mirrors nextStepsColumns in
 * backend/utils/report-helpers.js.
 */
export function nextStepsColumns(count, gapPct) {
  if (count <= 0) return [];
  const gaps = count - 1;
  const g = gaps > 0
    ? Math.max(0, Math.min(Math.round(Number(gapPct) || 0), Math.floor(50 / gaps)))
    : 0;
  const panelWidths = nextStepsWidths(count, 100 - g * gaps);
  const out = [];
  for (let i = 0; i < count; i++) {
    out.push({ panel: i, width: panelWidths[i] });
    if (i < count - 1 && g > 0) out.push({ gap: true, width: g });
  }
  return out;
}
